import React, { FC, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container } from '@components/atoms/Container';
import { Typography, TypographyAppearanceVariants, TypographyTagVariants } from '@components/atoms/Typography';
import { Footer } from '@components/molecules/Footer';
import { StickyHeader } from '@components/molecules/StickyHeader/StickyHeader';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { SITE_URLS } from '@constants';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { useData } from '@hooks/useData';
import sidebarStyles from './MyAccountSidebar.module.css';
import styles from './MyAccountContainer.module.css';
import { usePermissionChecker, UserPermissions } from '@hooks/useHasPermission';
import { Modules, useModule } from '@hooks/useModule';
import { useSettings } from '@hooks/useSettings';

type MyAccountContainerSubComponents = {
    Filters: typeof Filters;
};

interface MyAccountContainerProps {
    title: string;
    overrideOnDesktop?: JSX.Element;
    children: JSX.Element | JSX.Element[];
}

export const MyAccountContainer: FC<MyAccountContainerProps> & MyAccountContainerSubComponents = ({
    title,
    overrideOnDesktop,
    children,
}: MyAccountContainerProps) => {
    const { navigate } = useCustomNavigate();
    const { login } = useData();
    const { t } = useTranslation();

    const translations = {
        sidebarTitle: t('my-account-sidebar.title-my-account'),
    };

    // if user is not logged in, he can't access My Account area
    useEffect(() => {
        if (login === null) {
            navigate(`/${SITE_URLS.login}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [login]);

    return (
        <>
            <StickyHeader />

            <div className="position-relative" data-testid="my-account-container">
                <Container classes="px-3 mb-5 pt-5 py-lg-5">
                    <div className="d-flex gap-4">
                        <MyAccountSidebar />
                        <div className="my-account-content w-100 flex-grow-1">
                            {/* open sidebar */}
                            <Typography classes="d-lg-none mb-3">
                                <a
                                    className="text-decoration-none text-dark"
                                    data-bs-toggle="collapse"
                                    href="#collapseMerchantSidebar"
                                    role="button"
                                    aria-expanded="false"
                                    aria-controls="collapseMerchantSidebar">
                                    <i className="fa-light fa-arrow-left me-2" /> {translations.sidebarTitle}
                                </a>
                            </Typography>
                            {!overrideOnDesktop ? (
                                <Typography
                                    appearanceLevel={TypographyAppearanceVariants.level6}
                                    tagVariant={TypographyTagVariants.h1}>
                                    {title}
                                </Typography>
                            ) : (
                                overrideOnDesktop
                            )}

                            {children}
                        </div>
                    </div>
                </Container>
            </div>

            <Footer mode="normal" />
        </>
    );
};

interface FiltersProps {
    children: JSX.Element;
}

const Filters: FC<FiltersProps> = ({ children }) => {
    return (
        <div className={styles.filters} data-testid="my-account-filters">
            {children}
        </div>
    );
};

MyAccountContainer.Filters = Filters;

const MyAccountSidebar = () => {
    const { pathname } = useLocation();
    const { login } = useData();
    const { can } = usePermissionChecker();
    const { isModuleActive } = useModule();
    const { getPage } = useSettings();
    const { t } = useTranslation();

    const translations = {
        greeting: t('my-account-sidebar.greeting', { userName: login?.data?.user?.name || '' }),
        titleMyAccount: t('my-account-sidebar.title-my-account'),
        titleMerchants: t('my-account-sidebar.title-merchants'),
        titleLegalInformation: t('my-account-sidebar.title-legal-information'),
        legalTerms: t('policy-component.terms-and-conditions.title'),
        legalPrivacy: t('policy-component.privacy-policy.title'),
        dashboard: t('dashboard-component.title'),
        myProfile: t('my-profile-component.title'),
        myOrders: t('my-orders-component.title'),
        myProductReservations: t('my-product-reservations-component.title'),
        registerCompany: t('register-company-component.title'),
        myCompanies: t('my-companies-component.title'),
        manageProducts: t('manage-products-component.title'),
        manageServices: t('manage-services-component.title'),
        manageOrders: t('manage-orders-component.title'),
        manageProductReservations: t('manage-product-reservations-component.title'),
        manageAddresses: t('manage-addresses-component.title'),
        myCoupons: t('my-coupons-component.title'),
        myPoints: t('my-points-component.title'),
        promotions: t('promotions-component.title'),
        adhereToPromos: t('adhere-to-promos-component.title'),
        statistics: t('statistics-component.title'),
    };

    const canAccessMerchantAreas = can(UserPermissions.accessMerchantAreas);

    const merchantLinks = [
        canAccessMerchantAreas && {
            title: translations.dashboard,
            icon: 'fa-light fa-gauge',
            link: SITE_URLS.myDashboard,
        },
        canAccessMerchantAreas && {
            title: translations.myCompanies,
            icon: 'fa-light fa-suitcase',
            link: SITE_URLS.myCompanies,
        },
        canAccessMerchantAreas && {
            title: translations.manageProducts,
            icon: 'fa-light fa-tag',
            link: SITE_URLS.myProducts,
        },
        canAccessMerchantAreas && {
            title: translations.manageServices,
            icon: 'fa-light fa-briefcase',
            link: SITE_URLS.myServices,
        },
        canAccessMerchantAreas && {
            title: translations.manageOrders,
            icon: 'fa-light fa-boxes-stacked',
            link: SITE_URLS.manageOrders,
        },
        canAccessMerchantAreas && {
            title: translations.manageProductReservations,
            icon: 'fa-light fa-calendar-check',
            link: SITE_URLS.manageProductReservations,
        },
        canAccessMerchantAreas && {
            title: translations.adhereToPromos,
            icon: 'fa-light fa-plus',
            link: SITE_URLS.myAdherences,
        },
        !canAccessMerchantAreas && {
            title: translations.registerCompany,
            icon: 'fa-light fa-suitcase',
            link: SITE_URLS.myCompaniesCreate,
        },
    ].filter(Boolean) as { title: string; icon: string; link: string }[];

    const accountLinks = [
        { title: translations.myProfile, icon: 'fa-light fa-user-hair', link: SITE_URLS.myProfile },
        { title: translations.myOrders, icon: 'fa-light fa-boxes-stacked', link: SITE_URLS.myOrders },
        { title: translations.myProductReservations, icon: 'fa-light fa-calendar-check', link: SITE_URLS.myProductReservations },
        { title: translations.manageAddresses, icon: 'fa-light fa-house', link: SITE_URLS.myAddresses },
        canAccessMerchantAreas &&
            isModuleActive(Modules.coupon) && {
                title: translations.myCoupons,
                icon: 'fa-light fa-ticket',
                link: SITE_URLS.myCoupons,
            },
        isModuleActive(Modules.pointsGamification) && {
            title: translations.myPoints,
            icon: 'fa-light fa-gift',
            link: SITE_URLS.myPoints,
        },
    ].filter(Boolean) as { title: string; icon: string; link: string }[];

    const hasPolicyPageContent = (pageKey: string): boolean => {
        const page = getPage(pageKey);
        if (!page) return false;
        if (typeof page === 'string' && page.trim() !== '') return true;
        return false;
    };

    const legalLinks = [
        hasPolicyPageContent('terms-and-conditions') && {
            title: translations.legalTerms,
            icon: 'fa-light fa-file-lines',
            link: SITE_URLS.myAccountLegalPage.replace(':pageKey', 'terms-and-conditions'),
        },
        hasPolicyPageContent('privacy-policy') && {
            title: translations.legalPrivacy,
            icon: 'fa-light fa-shield',
            link: SITE_URLS.myAccountLegalPage.replace(':pageKey', 'privacy-policy'),
        },
    ].filter(Boolean) as { title: string; icon: string; link: string }[];

    type SidebarSection = { title: string; items: { title: string; icon: string; link: string }[] };

    const sidebarSections: SidebarSection[] = [
        merchantLinks.length > 0
            ? {
                  title: translations.titleMerchants,
                  items: merchantLinks,
              }
            : null,
        accountLinks.length > 0
            ? {
                  title: translations.titleMyAccount,
                  items: accountLinks,
              }
            : null,
    ].filter((section): section is SidebarSection => Boolean(section?.items?.length));

    const isLegalPath = useMemo(() => pathname.substring(1).startsWith(SITE_URLS.myAccountLegal), [pathname]);
    const [showOnlyLegal, setShowOnlyLegal] = useState<boolean>(isLegalPath);
    useEffect(() => {
        setShowOnlyLegal(isLegalPath);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLegalPath]);

    return (
        <div
            id="collapseMerchantSidebar"
            className={`${sidebarStyles['my-account-sidebar']} collapse collapse-horizontal sticky-top`}>
            <div
                className={`${sidebarStyles['sidebar-content']} d-lg-flex flex-column h-100 my-lg-0 me-lg-1 py-5 py-lg-0 pe-lg-4`}>
                <div className="container p-lg-0">
                    <Typography classes="d-lg-none mb-3" appearanceLevel={TypographyAppearanceVariants.level2}>
                        {translations.greeting}
                    </Typography>
                    {!showOnlyLegal && sidebarSections.map((section, sectionIndex) => (
                        <div key={`sidebar-section-${sectionIndex}`} className="mb-4">
                            <Typography appearanceLevel={TypographyAppearanceVariants.level7}>{section.title}</Typography>
                            <ul className="nav flex-column mb-0">
                                {section.items.map((item, index) => {
                                    const isActive = pathname.substring(1) === item.link;

                                    return (
                                        <li key={`menu-item-${sectionIndex}-${index}`}>
                                            <Anchor
                                                href={item.link}
                                                classes={`nav-link ${sidebarStyles['nav-link']} ${
                                                    isActive && item.link !== '' ? sidebarStyles.active : ''
                                                }`}
                                                mode={AnchorMode.none}>
                                                <>
                                                    <i className={item.icon} /> {item.title}
                                                </>
                                            </Anchor>
                                        </li>
                                    );
                                })}
                                {section.title === translations.titleMyAccount && legalLinks.length > 0 && (
                                    <li>
                                        <button
                                            type="button"
                                            className={`nav-link ${sidebarStyles['nav-link']} w-100 text-start`}
                                            onClick={() => setShowOnlyLegal(true)}>
                                            <i className="fa-light fa-shield" /> {translations.titleLegalInformation}
                                        </button>
                                    </li>
                                )}
                            </ul>
                        </div>
                    ))}
                    {showOnlyLegal && (
                        <div className="mb-4">
                            <Typography appearanceLevel={TypographyAppearanceVariants.level7}>
                                {translations.titleLegalInformation}
                            </Typography>
                            <ul className="nav flex-column mb-0">
                                <li>
                                    <button
                                        type="button"
                                        className={`nav-link ${sidebarStyles['nav-link']} w-100 text-start`}
                                        onClick={() => setShowOnlyLegal(false)}>
                                        <i className="fa-light fa-chevron-left me-2" /> {translations.titleMyAccount}
                                    </button>
                                </li>
                                {legalLinks.map((item, index) => {
                                    const isActive = pathname.substring(1) === item.link;
                                    return (
                                        <li key={`legal-item-${index}`}>
                                            <Anchor
                                                href={item.link}
                                                classes={`nav-link ${sidebarStyles['nav-link']} ${
                                                    isActive ? sidebarStyles.active : ''
                                                }`}
                                                mode={AnchorMode.none}>
                                                <>{item.title}</>
                                            </Anchor>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}
                </div>
                <div className="d-none d-lg-block sidebar-vertical-separator"></div>
            </div>
        </div>
    );
};
